import { motion } from "framer-motion";
import { RegistrationForm } from "../RegistrationForm";
import { Countdown } from "../Countdown";
import { EYEBROW, BONUSES } from "../../content";

const line = {
  hidden: { opacity: 0, y: 40, clipPath: "inset(0 0 100% 0)" },
  show: (i) => ({
    opacity: 1,
    y: 0,
    clipPath: "inset(0 0 -10% 0)",
    transition: { delay: 0.25 + i * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = ({ attribution }) => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative min-h-screen grid lg:grid-cols-2 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 z-0" aria-hidden="true">
        <img src="/images/hero-flag.jpg" alt="" className="w-full h-full object-cover" data-testid="hero-bg-image" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(18,38,74,0.96) 0%, rgba(18,38,74,0.88) 45%, rgba(18,38,74,0.72) 100%)" }} />
      </div>
      <div className="relative z-10 flex flex-col justify-center px-6 sm:px-10 lg:pl-16 xl:pl-24 pt-28 pb-12 lg:py-24">
        <div className="mb-6">
          <Countdown />
        </div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }} className="mb-8">
          <button
            data-testid="hero-top-cta"
            data-cta
            onClick={() => scrollTo("register")}
            className="bg-[#FFE100] text-[#12264A] font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-md btn-slide hover:-translate-y-0.5 transition-transform duration-200"
          >
            CLICK HERE TO START YOUR JOURNEY →
          </button>
        </motion.div>
        <div className="mono text-[13px] tracking-[0.35em] text-[#FFC631] mb-6" data-testid="hero-eyebrow">
          {EYEBROW}
        </div>
        <h1 className="serif font-black uppercase tracking-[-0.03em]" style={{ lineHeight: 0.95 }} data-testid="hero-h1">
          <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block text-[clamp(40px,5.2vw,76px)] text-white">
            DISCOVER HOW VA LOANS
          </motion.span>
          <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-[clamp(40px,5.2vw,76px)] text-[#FE0000]">
            WORK DIFFERENTLY—
          </motion.span>
          <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block text-[clamp(25px,3.2vw,47px)] text-white mt-2">
            AND WHY YOUR BENEFIT MATTERS MORE THAN YOUR CREDIT SCORE
          </motion.span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="text-[17px] text-white leading-relaxed mt-8 max-w-xl"
          data-testid="hero-subheadline"
        >
          The free webinar that shows <span className="text-[#FE0000] font-bold">Texas veterans</span> and{" "}
          <span className="text-[#FE0000] font-bold">active-duty service members</span> how to go from
          &ldquo;not yet approved&rdquo; to holding the keys.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7 }} className="mt-10">
          <button
            data-testid="hero-cta-button"
            data-cta
            onClick={() => scrollTo("register")}
            className="bg-[#FFE100] text-[#12264A] font-bold text-sm uppercase tracking-wide px-8 py-4 rounded-md btn-slide hover:-translate-y-0.5 transition-transform duration-200"
          >
            ✓ CLICK HERE TO START YOUR JOURNEY
          </button>
          <p className="mono text-[13px] text-white mt-3 tracking-wider">LOW TICKET NOTICE! — Seats fill fast</p>
        </motion.div>
      </div>

      <div className="relative flex items-center justify-center px-6 sm:px-10 lg:px-12 pb-28 lg:pb-12 lg:pt-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.9 }}
          className="relative z-10 w-full max-w-md rounded-xl border border-white/10 bg-[#132441]/75 backdrop-blur-xl p-6 sm:p-8 mb-14 md:mb-0"
          data-testid="hero-registration-card"
        >
          <div className="mono text-[13px] tracking-[0.35em] text-[#FFC631]">DON&apos;T MISS OUT</div>
          <h3 className="serif text-xl font-black uppercase tracking-tight text-white mt-3">
            When You Register, You&apos;ll Receive:
          </h3>
          <ul className="mt-4 space-y-2.5" data-testid="hero-bonus-list">
            {BONUSES.map((b, i) => (
              <li key={i} className="flex gap-3 text-[15px] text-white leading-snug">
                <span className="text-[#FE0000] shrink-0">✓</span>
                {b}
              </li>
            ))}
          </ul>
          <div className="text-[#FE0000] font-black text-[22px] mt-4 mb-4" data-testid="hero-free-label">FREE!</div>
          <RegistrationForm attribution={attribution} variant="hero" />
        </motion.div>
      </div>
    </section>
  );
};
